import React from 'react';
import { Phone, MapPin, Mail, Trash2, Eye } from 'lucide-react';
import authService from '../../../services/authService';

// Helper function to format address from structured fields
const formatAddress = (customer) => {
  const parts = [];
  
  if (customer.address_line1) parts.push(customer.address_line1);
  if (customer.address_line2) parts.push(customer.address_line2);
  if (customer.address_city) parts.push(customer.address_city);
  if (customer.address_county) parts.push(customer.address_county);
  if (customer.address_postcode) parts.push(customer.address_postcode);
  
  // Only include country if it's not UK (default)
  if (customer.address_country && customer.address_country !== 'United Kingdom') {
    parts.push(customer.address_country);
  }
  
  return parts.length > 0 ? parts.join(', ') : 'No address provided';
};

const CustomerTable = ({
  customers,
  isLoading,
  searchTerm,
  onCustomerSelect,
  onDeleteCustomer,
  onEditCustomer,
  userProfile
}) => {
  const isAdmin = userProfile?.role === 'admin';
  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pest-blue mx-auto"></div>
        <p className="text-gray-500 mt-2">Loading customers...</p>
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">
          {searchTerm ? 'No customers match your search.' : 'No customers added yet.'}
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-200">
      {customers.map((customer) => (
        <div
          key={customer.id}
          className="p-4 hover:bg-gray-50 transition-colors duration-200"
        >
          {/* Desktop Layout */}
          <div className="hidden md:flex items-center justify-between">
            <div 
              className="flex-1 cursor-pointer"
              onClick={() => onCustomerSelect(customer)}
            >
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-lg font-semibold text-gray-900">
                  {customer.company_name && customer.company_name !== customer.name ?
                    `${customer.name} (${customer.company_name})` :
                    customer.name
                  }
                </h3>
                <span className="text-xs font-mono text-gray-400">
                  {customer.customer_ref && <>ID: {customer.customer_ref}</>}
                  {customer.customer_ref && ' '}
                  QB: {customer.qb_customer_id || 'Awaiting Sync'}
                </span>
              </div>
              <div className="space-y-1">
                <div className="flex items-center text-gray-600">
                  <Phone size={16} className="mr-2" />
                  <span>{customer.phone}</span>
                </div>
                <div className="flex items-center text-gray-600">
                  <MapPin size={16} className="mr-2" />
                  <span>{formatAddress(customer)}</span>
                </div>
              </div>
            </div>
            <div className="text-right">
              {customer.email && (
                <div className="flex items-center justify-end text-gray-500 text-sm mb-1">
                  <Mail size={14} className="mr-1.5" />
                  <span>{customer.email}</span>
                </div>
              )}
              <div className="mt-2">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCustomerSelect(customer);
                      }}
                      className="btn btn-sm btn-secondary"
                      title={`View customer "${customer.name}"`}
                    >
                      <Eye size={12} />
                      <span>View Details</span>
                    </button>
                    {(userProfile?.role === 'admin' || authService.hasAdminReturn()) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteCustomer(customer);
                        }}
                        className="btn btn-sm btn-danger"
                        title={`Delete customer "${customer.name}"`}
                      >
                        <Trash2 size={14} />
                        <span>Delete</span>
                      </button>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 space-y-0.5 flex-1 text-right">
                    <div>Created: {new Date(customer.qb_created_date || customer.created_at).toLocaleDateString()}</div>
                    <div>Last activity: {new Date(customer.last_activity_at || customer.updated_at || customer.created_at).toLocaleDateString()}</div>
                    {isAdmin && customer.franchisee_company && (
                      <div className="text-purple-600 font-medium">{customer.franchisee_company}</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="md:hidden">
            <div
              className="cursor-pointer mb-3"
              onClick={() => onCustomerSelect(customer)}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {customer.company_name && customer.company_name !== customer.name ?
                  `${customer.name} (${customer.company_name})` :
                  customer.name
                }
              </h3>
              <div className="text-xs font-mono text-gray-400 mb-2">
                {customer.customer_ref && <>ID: {customer.customer_ref}</>}
                {customer.customer_ref && ' '}
                QB: {customer.qb_customer_id || 'Awaiting Sync'}
              </div>
              <div className="space-y-2">
                <div className="flex items-center text-gray-600">
                  <Phone size={16} className="mr-2 flex-shrink-0" />
                  <span className="text-sm">{customer.phone}</span>
                </div>
                <div className="flex items-start text-gray-600">
                  <MapPin size={16} className="mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-sm leading-relaxed">{formatAddress(customer)}</span>
                </div>
                {customer.email && (
                  <div className="flex items-center text-gray-500">
                    <Mail size={16} className="mr-2 flex-shrink-0" />
                    <span className="text-sm">{customer.email}</span>
                  </div>
                )}
              </div>
            </div>
            <div className="mt-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center space-x-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCustomerSelect(customer);
                    }}
                    className="btn btn-sm btn-secondary"
                    title={`View customer "${customer.name}"`}
                  >
                    <Eye size={12} />
                    <span>View</span>
                  </button>
                  {(userProfile?.role === 'admin' || authService.hasAdminReturn()) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteCustomer(customer);
                      }}
                      className="btn btn-sm btn-danger"
                      title={`Delete customer "${customer.name}"`}
                    >
                      <Trash2 size={14} />
                      <span>Delete</span>
                    </button>
                  )}
                </div>
                <div className="text-xs text-gray-400 space-y-0.5 flex-1 text-right">
                  <div>Created: {new Date(customer.qb_created_date || customer.created_at).toLocaleDateString()}</div>
                  <div>Last activity: {new Date(customer.qb_last_updated_date || customer.updated_at || customer.created_at).toLocaleDateString()}</div>
                  {isAdmin && customer.franchisee_company && (
                    <div className="text-purple-600 font-medium">{customer.franchisee_company}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CustomerTable;